
/**
 * The design studio. US-2.
 *
 * Everything the customer touches lives here; everything that decides money
 * lives on the server. The local engine drives the panel instantly (AC-2.3
 * asks for under 100ms), a debounced call to /api/estimate reconciles it, and
 * pressing "Request a quote" hands the whole configuration to quotes-submit,
 * which recomputes the estimate from scratch before writing anything down.
 */

import { UploadSimple } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DesignStage } from '@/components/studio/design-stage';
import { EstimatePanel } from '@/components/studio/estimate-panel';
import { Button, Callout, buttonClass } from '@/components/ui';
import { cn } from '@/lib/cn';
import { methodRowToEngine } from '@/lib/data/seed';
import { formatCents } from '@/lib/money';
import { LOCATIONS, computeEstimate, type Estimate } from '@/lib/pricing/engine';
import type { Placement, PrintMethodRow, Product, ReadyDesign } from '@/lib/types';

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

export interface StudioDraft {
  product_id: string;
  method_key: string;
  color: string;
  size: string;
  qty: number;
  colours: number;
  locations: string[];
  design_url: string | null;
  artwork_id: string | null;
  ready_design_id: string | null;
  placement: Placement;
}

const DRAFT_KEY = 'shopace.studio.draft.v1';

export function StudioClient({
  product,
  methods,
  readyDesigns,
  initialMethodKey,
}: {
  product: Product;
  methods: PrintMethodRow[];
  readyDesigns: ReadyDesign[];
  initialMethodKey?: string;
}) {
  const navigate = useNavigate();

  const [methodKey, setMethodKey] = useState(
    methods.find((m) => m.key === initialMethodKey)?.key ?? methods[0]!.key,
  );
  const [color, setColor] = useState(product.colors[0]!);
  const [size, setSize] = useState(product.sizes[0]!);
  const [qty, setQty] = useState(24);
  const [colours, setColours] = useState(1);
  const [locations, setLocations] = useState<string[]>(['front']);
  const [designUrl, setDesignUrl] = useState<string | null>(null);
  const [artworkId, setArtworkId] = useState<string | null>(null);
  const [readyDesignId, setReadyDesignId] = useState<string | null>(null);
  const [placement, setPlacement] = useState<Placement>({
    pos_x: 50,
    pos_y: product.type === 'cap' ? 50 : 48,
    scale: 60,
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const method = methods.find((m) => m.key === methodKey)!;

  /* ---------------------------------------------------------------------- */
  /* Estimate: local first, server reconciled                                */
  /* ---------------------------------------------------------------------- */

  const local: Estimate = useMemo(
    () =>
      computeEstimate(product.base_price_cents / 100, methodRowToEngine(method), {
        qty,
        colours,
        locations,
      }),
    [product.base_price_cents, method, qty, colours, locations],
  );

  const [server, setServer] = useState<Estimate | null>(null);
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setReconciling(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await fetch('/api/estimate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_id: product.id,
            method_key: methodKey,
            qty,
            colours,
            locations,
          }),
          signal: controller.signal,
        });
        if (res.ok) setServer((await res.json()) as Estimate);
      } catch {
        // Offline or aborted. The local preview stands until the next change.
      } finally {
        setReconciling(false);
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(t);
      setReconciling(false);
    };
  }, [product.id, methodKey, qty, colours, locations]);

  /* ---------------------------------------------------------------------- */
  /* Upload: presigned PUT via uploads-sign, contract §2                      */
  /* ---------------------------------------------------------------------- */

  async function upload(file: File) {
    setUploadError(null);

    if (!ACCEPTED.includes(file.type)) {
      setUploadError('Use a PNG, JPG, SVG or WEBP file.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('That file is over the 8 MB limit. Try exporting it smaller.');
      return;
    }

    setUploading(true);
    try {
      const signRes = await fetch('/api/uploads-sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          mime: file.type,
          size_bytes: file.size,
        }),
      });
      const signed = await signRes.json();
      if (!signRes.ok) {
        setUploadError(signed?.error?.message ?? 'That upload did not go through.');
        return;
      }

      // Presigned PUT straight to the S3 bucket, not R2 (R-32, §12.7). In
      // fixtures mode the sign endpoint hands
      // back a data URL instead, so the preview still works with no bucket.
      if (signed.upload_url && !signed.upload_url.startsWith('data:')) {
        await fetch(signed.upload_url, {
          method: 'PUT',
          headers: signed.headers,
          body: file,
        });
        setDesignUrl(signed.public_url);
      } else {
        setDesignUrl(await fileToDataUrl(file));
      }
      setArtworkId(signed.artwork_id ?? null);
      setReadyDesignId(null);
    } catch {
      setUploadError('That upload did not go through. Check your connection.');
    } finally {
      setUploading(false);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Hand off to the quote form                                              */
  /* ---------------------------------------------------------------------- */

  function requestQuote() {
    const draft: StudioDraft = {
      product_id: product.id,
      method_key: methodKey,
      color: color.name,
      size,
      qty,
      colours,
      locations,
      design_url: designUrl,
      artwork_id: artworkId,
      ready_design_id: readyDesignId,
      placement,
    };
    window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    navigate({ to: '/custom/quote' });
  }

  const optClass = (selected: boolean) =>
    cn(
      'inline-flex h-12 items-center gap-2 rounded-[10px] border px-3.5 text-[15px] font-semibold transition-colors',
      selected
        ? 'border-brand bg-brand-wash text-brand-ink'
        : 'border-line-strong bg-panel hover:border-ink/40',
    );

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_420px] lg:items-start">
      <div className="lg:sticky lg:top-24">
        <DesignStage
          type={product.type}
          colorHex={color.hex}
          designUrl={designUrl}
          placement={placement}
          onPlace={setPlacement}
        />
      </div>

      <div className="space-y-7">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-brand-ink">
            {product.category}
          </p>
          <h1 className="mt-1 text-[26px] font-extrabold tracking-[-0.03em]">{product.name}</h1>
          <p className="mt-1 text-2xl font-bold">{formatCents(product.base_price_cents)}</p>
          <p className="text-sm text-muted">Blank garment, before decoration</p>
        </div>

        {/* Colour --------------------------------------------------------- */}
        <fieldset>
          <legend className="text-sm font-semibold">
            Colour <span className="ml-1 font-normal text-muted">{color.name}</span>
          </legend>
          <div className="mt-2.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Colour">
            {product.colors.map((c) => (
              <button
                key={c.name}
                type="button"
                role="radio"
                aria-checked={color.name === c.name}
                onClick={() => setColor(c)}
                className={optClass(color.name === c.name)}
              >
                <span
                  aria-hidden
                  className="size-4 rounded-full border border-ink/20"
                  style={{ background: c.hex }}
                />
                {c.name}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Size ----------------------------------------------------------- */}
        <fieldset>
          <legend className="text-sm font-semibold">Size</legend>
          <div className="mt-2.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Size">
            {product.sizes.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={size === s}
                onClick={() => setSize(s)}
                className={cn(optClass(size === s), 'min-w-[52px] justify-center')}
              >
                {s}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Artwork -------------------------------------------------------- */}
        <div>
          <p className="text-sm font-semibold">Choose a ready-made design</p>
          <div className="mt-2.5 flex flex-wrap gap-3">
            {readyDesigns.map((d) => {
              const selected = readyDesignId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => {
                    setDesignUrl(d.image_url);
                    setReadyDesignId(d.id);
                    setArtworkId(null);
                  }}
                  aria-pressed={selected}
                  className={cn(
                    'checkerboard relative size-[92px] overflow-hidden rounded-[10px] border-2 p-2 transition-colors',
                    selected ? 'border-brand' : 'border-line-strong hover:border-ink/40',
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={d.image_url} alt={d.name} className="h-full w-full object-contain" />
                  {selected && (
                    <span
                      aria-hidden
                      className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-brand-strong text-[11px] font-bold text-on-brand"
                    >
                      &#10003;
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold">Or upload your own design</p>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files[0];
              if (f) void upload(f);
            }}
            className={cn(
              'mt-2.5 rounded-[10px] border-2 border-dashed px-5 py-7 text-center transition-colors',
              dragOver ? 'border-brand bg-brand-wash' : 'border-line-strong',
            )}
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED.join(',')}
              className="sr-only"
              id="artwork-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
              }}
            />
            <label htmlFor="artwork-file" className={buttonClass('secondary', 'md', 'cursor-pointer')}>
              <UploadSimple size={18} weight="bold" aria-hidden />
              {uploading ? 'Uploading' : 'Choose a file'}
            </label>
            <p className="mt-3 text-sm text-muted">
              or drag and drop. PNG, JPG, SVG or WEBP up to 8 MB. PNG with transparency
              works best.
            </p>
          </div>
          {uploadError && (
            <div className="mt-3">
              <Callout tone="danger" title={uploadError} />
            </div>
          )}
        </div>

        {/* Design size ---------------------------------------------------- */}
        <div>
          <label htmlFor="design-scale" className="text-sm font-semibold">
            Design size
          </label>
          <div className="mt-2.5 flex items-center gap-4">
            <input
              id="design-scale"
              type="range"
              min={15}
              max={100}
              step={1}
              value={placement.scale}
              onChange={(e) => setPlacement((p) => ({ ...p, scale: Number(e.target.value) }))}
              className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-line-strong accent-[var(--color-brand-strong)]"
            />
            <output className="w-12 text-right font-mono text-sm font-bold">
              {placement.scale}%
            </output>
          </div>
        </div>

        <hr className="border-line" />

        {/* Method --------------------------------------------------------- */}
        <fieldset>
          <legend className="text-sm font-semibold">Print method</legend>
          <div
            className="mt-2.5 grid gap-2.5 sm:grid-cols-2"
            role="radiogroup"
            aria-label="Print method"
          >
            {methods.map((m) => {
              const selected = m.key === methodKey;
              return (
                <button
                  key={m.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setMethodKey(m.key);
                    if (m.max_colours != null && colours > m.max_colours) {
                      setColours(m.max_colours);
                    }
                  }}
                  className={cn(
                    'rounded-[10px] border p-3.5 text-left transition-colors',
                    selected
                      ? 'border-brand bg-brand-wash'
                      : 'border-line-strong bg-panel hover:border-ink/40',
                  )}
                >
                  <span className="block text-[15px] font-bold">{m.name}</span>
                  <span className="mt-0.5 block text-sm text-muted">
                    {m.turnaround} &middot;{' '}
                    {m.max_colours == null ? 'Full colour' : `up to ${m.max_colours} colours`}
                  </span>
                  {m.specialty && (
                    <span className="mt-1 block text-sm font-semibold text-brand-ink">
                      {m.specialty}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-2.5 text-sm text-muted">{method.blurb}</p>
        </fieldset>

        {/* Colours: hidden for full-colour methods, as in the prototype ---- */}
        {method.max_colours != null && (
          <div>
            <label htmlFor="colours" className="text-sm font-semibold">
              Design colours
            </label>
            <p className="mt-1 text-[13px] text-muted">
              {method.name} supports up to {method.max_colours}.
            </p>
            <input
              id="colours"
              type="number"
              min={1}
              max={method.max_colours}
              value={colours}
              onChange={(e) => setColours(Math.max(1, Number(e.target.value) || 1))}
              className="mt-2 h-12 w-28 rounded-[10px] border border-line-strong bg-panel px-3.5 font-mono text-[15px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25"
            />
          </div>
        )}

        {/* Locations ------------------------------------------------------ */}
        <fieldset>
          <legend className="text-sm font-semibold">Print locations</legend>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {LOCATIONS.map((l) => {
              const on = locations.includes(l.key);
              return (
                <button
                  key={l.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setLocations((cur) => {
                      const next = on ? cur.filter((x) => x !== l.key) : [...cur, l.key];
                      return next.length ? next : ['front'];
                    })
                  }
                  className={optClass(on)}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Quantity ------------------------------------------------------- */}
        <div>
          <label htmlFor="qty" className="text-sm font-semibold">
            Quantity
          </label>
          <input
            id="qty"
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
            className="mt-2 h-12 w-32 rounded-[10px] border border-line-strong bg-panel px-3.5 font-mono text-[15px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25"
          />
        </div>

        <EstimatePanel local={local} server={server} reconciling={reconciling} />

        <Button onClick={requestQuote} size="lg" className="w-full">
          Request a quote
        </Button>
      </div>
    </div>
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export { DRAFT_KEY };
